import { motion } from "framer-motion";
import {
  PhoneMissed, AlertTriangle, Clock, ReceiptText, TrendingDown, Bell, Hourglass, MessageCircleWarning,
} from "lucide-react";

/**
 * Cinematic "operational chaos" composition — abstract, no stock photos or robots.
 * A dim central axis with warning tiles orbiting it, layered radial glow, and
 * subtle grid to give the feeling of a control tower going out of control.
 */
export default function ExecProblemCanvas() {
  return (
    <div className="relative w-full aspect-[10/9] max-w-[720px]">
      {/* Cinematic backdrop */}
      <div className="absolute inset-0 rounded-[24px] overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(closest-side at 55% 60%, rgba(212,160,23,0.16), transparent 65%), radial-gradient(closest-side at 20% 20%, rgba(228,88,88,0.10), transparent 70%), linear-gradient(180deg, #0A2226 0%, #071A1E 100%)",
          }}
        />
        <div className="absolute inset-0 exec-grid-bg opacity-70" />
        {/* Vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 90% at 50% 50%, transparent 40%, rgba(0,0,0,0.55) 100%)",
          }}
        />
      </div>

      {/* Central "founder / axis" silhouette — abstract geometric */}
      <div className="absolute left-1/2 top-[54%] -translate-x-1/2 -translate-y-1/2">
        <div className="relative w-[220px] h-[220px]">
          {/* Pulse rings */}
          <span className="absolute inset-0 rounded-full border border-white/[0.05]" />
          <span className="absolute inset-6 rounded-full border border-white/[0.05]" />
          <span className="absolute inset-12 rounded-full border border-exec-gold/15" />
          {/* Core dim orb */}
          <div
            className="absolute inset-[74px] rounded-full"
            style={{
              background:
                "radial-gradient(closest-side, rgba(212,160,23,0.30), rgba(212,160,23,0.10) 50%, transparent 75%)",
              boxShadow: "inset 0 0 30px rgba(212,160,23,0.35), 0 0 60px -10px rgba(212,160,23,0.35)",
            }}
          />
          {/* subtle overlay lines */}
          <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 220 220" fill="none">
            <circle cx="110" cy="110" r="90" stroke="rgba(255,255,255,0.05)" strokeDasharray="2 4" />
            <circle cx="110" cy="110" r="60" stroke="rgba(255,255,255,0.05)" strokeDasharray="2 4" />
          </svg>
          {/* Label */}
          <div className="absolute -bottom-8 inset-x-0 text-center">
            <div className="text-[10px] tracking-[0.24em] uppercase text-exec-mute-2 font-medium">
              Operational Overload
            </div>
          </div>
        </div>
      </div>

      {/* Orbiting warning cards */}
      {CARDS.map((c, i) => (
        <motion.div
          key={c.label}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.06 * i }}
          className={`absolute ${c.pos} exec-card-solid px-3.5 py-2.5 flex items-center gap-2.5 ${c.float}`}
          style={{
            background: "linear-gradient(180deg, rgba(23,58,63,0.9) 0%, rgba(15,45,49,0.9) 100%)",
            borderColor: "rgba(255,255,255,0.06)",
          }}
        >
          <div
            className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${
              c.tone === "warn"
                ? "bg-exec-gold/12 text-exec-gold-2"
                : c.tone === "danger"
                ? "bg-[#E45858]/12 text-[#F58585]"
                : "bg-white/[0.05] text-exec-ink-2"
            }`}
          >
            <c.icon className="w-4 h-4" strokeWidth={1.6} />
          </div>
          <div className="min-w-0">
            <div className="text-[12px] text-exec-ink font-medium leading-tight">{c.label}</div>
            <div className="text-[10.5px] text-exec-mute-2 mt-0.5">{c.sub}</div>
          </div>
        </motion.div>
      ))}

      {/* Bottom caption */}
      <div className="absolute bottom-4 inset-x-0 text-center">
        <div className="text-[12.5px] italic text-exec-mute">
          Too much to manage. Too little time to grow.
        </div>
      </div>
    </div>
  );
}

const CARDS = [
  { label: "Missed Call",         sub: "Customer follow-up",  icon: PhoneMissed,          tone: "danger", pos: "top-8 left-4",             float: "exec-float-slow" },
  { label: "Invoice Overdue",     sub: "₹1,25,000",           icon: ReceiptText,          tone: "warn",   pos: "top-6 right-4",            float: "exec-float-med"  },
  { label: "Payment Due",         sub: "2 vendors",           icon: AlertTriangle,        tone: "warn",   pos: "top-32 left-0",            float: "exec-float-fast" },
  { label: "Task Due Today",      sub: "12 pending",          icon: Clock,                tone: "warn",   pos: "top-40 right-2",           float: "exec-float-slow" },
  { label: "Low Cash Flow",       sub: "Action required",     icon: TrendingDown,         tone: "danger", pos: "bottom-24 left-6",         float: "exec-float-med"  },
  { label: "Appointment Pending", sub: "Reschedule",          icon: Hourglass,            tone: "warn",   pos: "bottom-32 right-4",        float: "exec-float-fast" },
  { label: "Reminder",            sub: "3 more today",        icon: Bell,                 tone: "neutral",pos: "top-[245px] left-2",           float: "exec-float-slow" },
  { label: "Customer Waiting",    sub: "6 min in queue",      icon: MessageCircleWarning, tone: "danger", pos: "bottom-8 left-[60%]",      float: "exec-float-med" },
];
